import { useId, type InputHTMLAttributes } from 'react';

import type { AddressSuggestion } from '../api/address-suggestions';
import { VisuallyHiddenStatus } from '../components/shared/VisuallyHiddenStatus';

import { AddressSuggestionList } from './AddressSuggestionList';
import { suggestionNote, useAddressAutocomplete } from './use-address-autocomplete';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'role'> & {
  /** Required: the suggestion list sits inside the field's label, so the
   *  label's own text would otherwise read out every suggestion too. */
  'aria-label': string;
  value: string;
  onChange: (text: string) => void;
  onPick: (address: AddressSuggestion) => void;
};

/** A street-address box that offers matching addresses as you type. Picking
 *  one hands back every part of it; typing freely still works, and without a
 *  lookup token it is a plain text box. */
export function AddressAutocompleteInput({ value, onChange, onPick, ...rest }: Props) {
  const uid = useId();
  const listId = `${uid}-list`;
  const box = useAddressAutocomplete(onPick);

  if (!box.available) {
    return (
      <input
        {...rest}
        autoComplete="address-line1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const activeId = box.suggestions[box.active] ? `${listId}-${box.active}` : undefined;
  const count = box.suggestions.length;
  return (
    <div style={{ position: 'relative' }}>
      <input
        {...rest}
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={box.listOpen}
        aria-controls={listId}
        aria-activedescendant={box.listOpen ? activeId : undefined}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          box.search(e.target.value);
        }}
        onKeyDown={box.onKeyDown}
        onBlur={box.close}
      />
      {box.listOpen && (
        <AddressSuggestionList
          id={listId}
          suggestions={box.suggestions}
          active={box.active}
          status={box.status}
          onPick={box.pick}
        />
      )}
      <VisuallyHiddenStatus
        message={
          box.listOpen ? (suggestionNote(box.status, count) ?? `${count} addresses suggested`) : ''
        }
      />
    </div>
  );
}
